/**
 * Products API Client
 *
 * Provides methods for managing insurance products.
 * All fetch methods unwrap the standard API envelope
 * { success, message, timestamp, data: T } and return T directly.
 */

import type { InsuranceProduct } from "@neutrino/ui-equinox-types";
import type { ProductFormData } from "../types/products.types";

// ─── Response types (unwrapped — match the `data` field of the API envelope) ─

export interface ProductsResponse {
  products: InsuranceProduct[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// ─── Fetch infrastructure ─────────────────────────────────────────────────────

export type ProductsFetchFunction = (
  url: string,
  options?: RequestInit,
) => Promise<Response>;

function defaultFetch(url: string, options?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
}

let _fetch: ProductsFetchFunction = defaultFetch;

export function configureProductsApi(fetchFn: ProductsFetchFunction): void {
  _fetch = fetchFn;
}

async function unwrap<T>(response: Response): Promise<T> {
  const body = await response.json();
  if (!response.ok) {
    throw new Error(
      body?.error?.message || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return body.data as T;
}

// ─── API client ───────────────────────────────────────────────────────────────

export const productsApi = {
  async fetchAll(
    params?: {
      limit?: number;
      offset?: number;
      status?: string;
      category?: string;
      search?: string;
    },
    baseUrl = "/api/v1",
  ): Promise<ProductsResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    if (params?.status) query.set("status", params.status);
    if (params?.category) query.set("category", params.category);
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    const res = await _fetch(`${baseUrl}/products${qs ? `?${qs}` : ""}`);
    return unwrap<ProductsResponse>(res);
  },

  async fetchById(
    productId: string,
    baseUrl = "/api/v1",
  ): Promise<InsuranceProduct> {
    const res = await _fetch(`${baseUrl}/products/${productId}`);
    return unwrap<InsuranceProduct>(res);
  },

  async createProduct(
    data: ProductFormData,
    baseUrl = "/api/v1",
  ): Promise<InsuranceProduct> {
    const res = await _fetch(`${baseUrl}/admin/products`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return unwrap<InsuranceProduct>(res);
  },

  async updateProduct(
    productId: string,
    data: Partial<ProductFormData>,
    baseUrl = "/api/v1",
  ): Promise<InsuranceProduct> {
    const res = await _fetch(`${baseUrl}/admin/products/${productId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return unwrap<InsuranceProduct>(res);
  },

  async deleteProduct(productId: string, baseUrl = "/api/v1"): Promise<void> {
    const res = await _fetch(`${baseUrl}/admin/products/${productId}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const body = await res.json();
      throw new Error(
        body?.error?.message || `HTTP ${res.status}: ${res.statusText}`,
      );
    }
  },
};
